import DeveloperIcon from '@assets/icons/developers.svg?react'
import LogoutIcon from '@assets/icons/logout.svg?react'
import MoonIcon from '@assets/icons/moon.svg?react'
import SettingsIcon from '@assets/icons/settings.svg?react'
import SunIcon from '@assets/icons/sun.svg?react'
import UserIcon from '@assets/icons/user.svg?react'
import { ThemeContext } from '@context/ThemeContext'
import { useMenuItems } from '@hooks'
import KingdomBankLogo from '@images/kingdom-bank-icon-black.svg?react'
import cn from 'classnames'
import {
  isOwnerOrAdministrator,
  MerchantEntityTypes,
  paths,
  useAuth,
  useLogoutQry,
  useMerchantQry,
  useUserProfileQry
} from 'mmfintech-backend-api'
import { tr } from 'mmfintech-commons'
import { type BaseSyntheticEvent, useContext } from 'react'
import { useHistory } from 'react-router-dom'

import { CompanySwitcher, useCompanySwitch } from './ProfileMenu'

import './HamburgerMenu.scss'

type HamburgerMenuProps = {
  opened: boolean
  maxWidth?: string
  onClose: () => void
}

export const HamburgerMenu = ({ opened, maxWidth, onClose }: HamburgerMenuProps) => {
  const { darkTheme, toggleTheme } = useContext(ThemeContext)

  const history = useHistory()

  const userStatus = useAuth()
  const isLoggedIn = () => userStatus === 'LOGGED_IN'

  const { merchant } = useMerchantQry()
  const { profile } = useUserProfileQry()

  const { logout } = useLogoutQry(true)

  const isDevelopersMenuVisible = () => merchant?.apiEnabled && isOwnerOrAdministrator(profile?.customerRole)

  const items = useMenuItems()

  const { companies, canSwitchCompany, currentMerchantId, parentMerchantId, switchCompany } = useCompanySwitch()

  const handleLogoutClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    void logout()
    return false
  }

  return (
    <div
      className={cn('hamburger-menu-wrapper', {
        open: opened,
        maxMobileWidth: maxWidth
      })}
    >
      <div className='hamburger-menu-content'>
        <div className='hamburger-menu-header'>
          <KingdomBankLogo />
        </div>
        {isLoggedIn() && canSwitchCompany && (
          <div className='hamburger-company-switcher'>
            <CompanySwitcher
              companies={companies}
              currentMerchantId={currentMerchantId}
              parentMerchantId={parentMerchantId}
              onSelect={targetMerchantId => {
                onClose()
                void switchCompany(targetMerchantId)
              }}
            />
          </div>
        )}
        {isLoggedIn() && (
          <div className='menu-items-container'>
            {items.map(({ icon: Icon, label, path, localization }) => {
              return (
                <div
                  className='item'
                  key={path}
                  onClick={() => {
                    onClose()
                    history.push(path)
                  }}
                >
                  <Icon />
                  <span>{tr(localization, label)}</span>
                </div>
              )
            })}
          </div>
        )}
        {isLoggedIn() && (
          <div className='menu-footer'>
            {isOwnerOrAdministrator(profile?.customerRole) && merchant?.entityType === MerchantEntityTypes.COMPANY && (
              <div
                onClick={e => {
                  onClose()
                  e.preventDefault()
                  history.push(paths.userDepartmentManagement())
                }}
                className='item'
              >
                <UserIcon />
                <span>{tr('FRONTEND.HEADER.USER_MANAGEMENT', 'User management')}</span>
              </div>
            )}

            {isDevelopersMenuVisible() && (
              <div
                className='item'
                onClick={e => {
                  onClose()
                  e.preventDefault()
                  history.push(paths.developer())
                }}
              >
                <DeveloperIcon />
                <span>{tr('FRONTEND.HEADER.DEVELOPERS', 'Developers')}</span>
              </div>
            )}

            <div
              className='item'
              onClick={e => {
                onClose()
                e.preventDefault()
                history.push(paths.profile())
              }}
            >
              <SettingsIcon />
              <span>{tr('FRONTEND.HEADER.PROFILE_SETTINGS', 'Profile settings')}</span>
            </div>

            <div
              className='item'
              onClick={e => {
                e.preventDefault()
                toggleTheme()
              }}
            >
              {darkTheme ? <SunIcon /> : <MoonIcon />}
              <span>
                {darkTheme
                  ? tr('FRONTEND.HAMBURGER_MENU.LIGHT_MODE', 'Light mode')
                  : tr('FRONTEND.HAMBURGER_MENU.DARK_MODE', 'Dark mode')}
              </span>
            </div>

            <div
              onClick={e => {
                handleLogoutClick(e)
                onClose()
              }}
              className='item logout'
            >
              <LogoutIcon />
              <span>{tr('FRONTEND.HAMBURGER_MENU.LOG_OUT', 'Log out')}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
